import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Description, Typography } from '@heroui/react';
import { TextContext } from 'react-aria-components/Text';
import type { VariantOption } from '../../useSeller';
import { formatGel } from '../../sellerMeta';

type VariantPickerOptionContentProps = {
    option: VariantOption;
};

/**
 * One variant picker row's content: its label over its stock state — the
 * sellable quantity, or a chip saying why it can't be chosen (out of stock,
 * or all of it already in the cart) — and its price, with how many the cart
 * holds. Content only: the choosable row around it is its `ListBox.Item` in
 * `VariantPickerModal`.
 */
export const VariantPickerOptionContent: React.FC<
    VariantPickerOptionContentProps
> = ({ option }) => {
    const { t } = useTranslation('pages');

    return (
        <>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-1">
                <Typography slot="label" weight="medium">
                    {option.label}
                </Typography>
                {option.availability === 'outOfStock' ? (
                    <Chip
                        className="self-start"
                        color="danger"
                        size="sm"
                        variant="soft"
                    >
                        {t('seller.outOfStock')}
                    </Chip>
                ) : option.availability === 'allInCart' ? (
                    <Chip
                        className="self-start"
                        color="warning"
                        size="sm"
                        variant="soft"
                    >
                        {t('seller.variantPicker.allInCart')}
                    </Chip>
                ) : (
                    <Description className="tabular-nums">
                        {t('seller.inStock', {
                            count: option.sellableQuantity,
                        })}
                    </Description>
                )}
            </div>
            {/* The item hands its label slot to any unslotted text, so the
                price opts out: the variant label names the option. */}
            <TextContext.Provider value={null}>
                <div className="flex flex-col items-end gap-1">
                    <Typography className="tabular-nums" weight="bold">
                        {formatGel(option.price)}
                    </Typography>
                    {option.inCartQty > 0 ? (
                        <Chip color="accent" size="sm" variant="soft">
                            {t('seller.variantPicker.inCart', {
                                count: option.inCartQty,
                            })}
                        </Chip>
                    ) : null}
                </div>
            </TextContext.Provider>
        </>
    );
};
